import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Grid,
  Table,
  CircularProgress,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Typography,
  Switch,
  FormControlLabel,
  Chip,
  TablePagination,
  Snackbar,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Delete, Edit, Add, FilterAltOff } from '@mui/icons-material';
import { categoriesAPI } from '../../services/api';
import browserGlobal from '../../utils/browserGlobal';
import { clearFilterButtonSx } from '../../utils/clearFilterButtonSx';
import { useLanguage } from '../../context/LanguageContext';
import { getAdminUser } from '../../utils/authSessionStorage';
import { canManageContacts, canDeleteContacts } from '../../utils/roleUtils';

const DEFAULT_CATEGORY_FORM = {
  backgroundColor: '#c84c4c',
  imageBackgroundColor: '#b11b1b',
  icon: '',
  title_EN: '',
  title_DE: '',
  discription_EN: '',
  discription_DE: '',
  isInfoIconAvailable: true,
  hasContact: true,
  isEmergencyContact: true,
  categoryFor: false,
  categoryType: 0,
};

const CATEGORY_SWITCH_SX = {
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: '#0F3D3E',
  },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: '#0F3D3E',
  },
};

function normalizeSearchText(value) {
  return String(value ?? '').toLowerCase();
}

function categoryMatchesSearch(category, query) {
  return (
    normalizeSearchText(category.title_EN).includes(query) ||
    normalizeSearchText(category.title_DE).includes(query) ||
    normalizeSearchText(category.discription_EN).includes(query) ||
    normalizeSearchText(category.discription_DE).includes(query) ||
    normalizeSearchText(category.icon).includes(query)
  );
}

function getCategoryListTotal(apiTotal, listLength) {
  if (apiTotal != null && Number(apiTotal) >= 0) {
    return Number(apiTotal);
  }
  return listLength;
}

function createCategoryFormDataFromRecord(category) {
  return {
    backgroundColor: category.backgroundColor || DEFAULT_CATEGORY_FORM.backgroundColor,
    imageBackgroundColor: category.imageBackgroundColor || DEFAULT_CATEGORY_FORM.imageBackgroundColor,
    icon: category.icon || '',
    title_EN: category.title_EN || '',
    title_DE: category.title_DE || '',
    discription_EN: category.discription_EN || '',
    discription_DE: category.discription_DE || '',
    isInfoIconAvailable: Boolean(category.isInfoIconAvailable),
    hasContact: Boolean(category.hasContact),
    isEmergencyContact: Boolean(category.isEmergencyContact),
    categoryFor: category.categoryFor === 1 || category.categoryFor === '1' || category.categoryFor === true || category.categoryFor === 'true',
    categoryType: category.categoryType || 0,
  };
}

function normalizeBooleanFormValue(value) {
  return value === true || value === 'true';
}

function buildCategorySubmitPayload(formData, selectedCategory) {
  const categoryForVal = (formData.categoryFor === true || formData.categoryFor === 1) ? 1 : 0;
  let categoryTypeVal = 0;
  if (categoryForVal === 1) {
    if (selectedCategory && selectedCategory.id) {
      categoryTypeVal = selectedCategory.id;
    } else {
      categoryTypeVal = 0;
    }
  } else {
    categoryTypeVal = 0;
  }

  return {
    ...formData,
    categoryFor: categoryForVal,
    categoryType: categoryTypeVal,
    isInfoIconAvailable: normalizeBooleanFormValue(formData.isInfoIconAvailable),
    hasContact: normalizeBooleanFormValue(formData.hasContact),
    isEmergencyContact: normalizeBooleanFormValue(formData.isEmergencyContact),
  };
}

function getYesNoChipLabel(value, translate) {
  if (value) return translate('common.yes');
  return translate('common.no');
}

function getYesNoChipColor(value) {
  if (value) return 'success';
  return 'default';
}

function getCategoryDialogTitle(selectedCategory, translate) {
  if (selectedCategory) return translate('categoriesPage.dialogEdit');
  return translate('categoriesPage.dialogAdd');
}

function getCategorySubmitLabel(selectedCategory, translate) {
  if (selectedCategory) return translate('common.update');
  return translate('common.create');
}

function CategoryBooleanSwitch({ name, checked, label, onChange }) {
  return (
    <FormControlLabel
      control={
        <Switch
          name={name}
          checked={checked}
          onChange={onChange}
          sx={CATEGORY_SWITCH_SX}
        />
      }
      label={label}
    />
  );
}

const Categories = () => {
  const { t } = useLanguage();
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down('sm'));
  const adminUser = getAdminUser();
  const canAddEdit = canManageContacts(adminUser?.role);
  const canDelete = canDeleteContacts(adminUser?.role);

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [open, setOpen] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debouncedSearchRef = useRef(debouncedSearch);
  useEffect(() => {
    debouncedSearchRef.current = debouncedSearch;
  }, [debouncedSearch]);

  useEffect(() => {
    const id = browserGlobal.setTimeout(() => {
      const next = (searchQuery || '').trim();
      const prev = debouncedSearchRef.current;
      if (prev !== next) {
        setPage(0);
      }
      setDebouncedSearch(next);
    }, 400);
    return () => browserGlobal.clearTimeout(id);
  }, [searchQuery]);

  const [toastOpen, setToastOpen] = useState(false);
  const [toast, setToast] = useState({ text: '', type: 'info' });

  const [formData, setFormData] = useState({ ...DEFAULT_CATEGORY_FORM });

  const showToast = (text, type = 'info') => {
    setToast({ text, type });
    setToastOpen(true);
  };

  const handleSwitchChange = (fieldName) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: event.target.checked,
    }));
  };

  const fetchCategories = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await categoriesAPI.getCategories(null, page, rowsPerPage, {
        title: debouncedSearch,
      });
      if (response?.data?.success && Array.isArray(response?.data?.payload)) {
        const list = response.data.payload;
        setCategories(list);
        const apiTotal = response?.data?.totalRecord;
        setTotalCount(getCategoryListTotal(apiTotal, list.length));
      } else {
        setCategories([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearch]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const visibleCategories = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return categories;
    return categories.filter((category) => categoryMatchesSearch(category, q));
  }, [categories, debouncedSearch]);

  const handleOpen = (category = null) => {
    if (category) {
      setSelectedCategory(category);
      setFormData(createCategoryFormDataFromRecord(category));
    } else {
      setSelectedCategory(null);
      setFormData({ ...DEFAULT_CATEGORY_FORM });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedCategory(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const payload = buildCategorySubmitPayload(formData, selectedCategory);
      if (selectedCategory) {
        await categoriesAPI.updateCategory({ ...payload, id: selectedCategory.id });
        showToast(t('categoriesPage.toastUpdated'), 'success');
      } else {
        await categoriesAPI.createCategory(payload);
        showToast(t('categoriesPage.toastCreated'), 'success');
      }
      fetchCategories();
      handleClose();
    } catch (error) {
      console.error('Error saving category:', error);
      showToast(error?.response?.data?.errorMessage || t('categoriesPage.toastSaveError'), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (browserGlobal.confirm(t('categoriesPage.confirmDelete'))) {
      setIsLoading(true);
      try {
        await categoriesAPI.deleteCategory(id);
        showToast(t('categoriesPage.toastDeleted'), 'success');
        fetchCategories();
      } catch (error) {
        console.error('Error deleting category:', error);
        showToast(error?.response?.data?.errorMessage || t('categoriesPage.toastDeleteError'), 'error');
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <Box p={1}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        mb={2}
        flexDirection={{ xs: 'column', sm: 'row' }}
        flexWrap="wrap"
        gap={1}
      >
        <Typography variant="h4" color="primary">
          {t('categoriesPage.title')}
        </Typography>
        {canAddEdit && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpen()}
            sx={{
              background: 'linear-gradient(135deg, #1b5a5c 0%, #0a2829 100%)',
              color: '#ffffff',
              '&:hover': {
                background: 'linear-gradient(135deg, #328284 0%, #0F3D3E 100%)',
              },
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            {t('categoriesPage.add')}
          </Button>
        )}
      </Box>
      <Box
        sx={{
          mb: 3,
          p: 2,
          bgcolor: '#fff',
          borderRadius: 1,
          boxShadow: '0 2px 4px rgba(124, 58, 237, 0.05)',
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              size="small"
              fullWidth
              placeholder={t('categoriesPage.searchPh')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#0F3D3E',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#0F3D3E',
                  },
                },
              }}
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            md="auto"
            sx={{ display: 'flex', alignItems: 'center', ml: { md: 'auto' } }}
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterAltOff />}
              onClick={() => {
                setSearchQuery('');
                setDebouncedSearch('');
              }}
              sx={{ ...clearFilterButtonSx, width: { xs: '100%', md: 'auto' } }}
            >
              {t('common.clear')}
            </Button>
          </Grid>
        </Grid>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress sx={{ color: '#0F3D3E' }} />
        </Box>
      ) : (
        <TableContainer 
          component={Paper}
          sx={{
            overflowX: 'auto',
            boxShadow: '0 2px 4px rgba(124, 58, 237, 0.05)',
            borderRadius: 2,
            '& .MuiTableCell-head': {
              backgroundColor: '#ebf4f4',
              color: '#0F3D3E',
              fontWeight: 600,
              padding: '16px'
            },
            '& .MuiTableCell-body': {
              padding: '16px'
            }
          }}
        >
          <Table sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow>
                <TableCell>{t('categoriesPage.colTitle')}</TableCell>
                <TableCell>{t('categoriesPage.colDesc')}</TableCell>
                <TableCell>{t('categoriesPage.colColors')}</TableCell>
                <TableCell>{t('categoriesPage.colIcon')}</TableCell>
                <TableCell>{t('categoriesPage.colContact')}</TableCell>
                <TableCell>{t('common.status')}</TableCell>
                <TableCell>{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleCategories.map((category) => (
                <TableRow 
                  key={category.id}
                  sx={{
                    '&:hover': {
                      backgroundColor: '#ebf4f4',
                    }
                  }}
                >
                  <TableCell>
                    <Box>
                      <Typography>{category.title_EN}</Typography>
                      <Typography variant="caption" color="text.secondary">{category.title_DE}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography>{category.discription_EN}</Typography>
                      <Typography variant="caption" color="text.secondary">{category.discription_DE}</Typography>
                    </Box>
                  </TableCell>
                 
                  <TableCell>
                    <Box display="flex" gap={1} alignItems="center">
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: 1,
                          bgcolor: category.backgroundColor,
                          border: '1px solid rgba(0,0,0,0.1)'
                        }}
                      />
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: 1,
                          bgcolor: category.imageBackgroundColor,
                          border: '1px solid rgba(0,0,0,0.1)'
                        }}
                      />
                    </Box>
                  </TableCell>
                   <TableCell>
                    <Box>
                      <Typography>{category.icon}</Typography>
                      {t('categoriesPage.infoIconPrefix')} :
                      <Chip
                          label={getYesNoChipLabel(category.isInfoIconAvailable, t)}
                          color={getYesNoChipColor(category.isInfoIconAvailable)}
                          size="small"
                    />
                    </Box>
                  </TableCell>
                  <TableCell>
                  <Box>
                      {t('categoriesPage.hasPrefix')} : 
                      <Chip
                          label={getYesNoChipLabel(category.hasContact, t)}
                          color={getYesNoChipColor(category.hasContact)}
                          size="small"
                     />
                     <Typography>
                      {t('categoriesPage.emergencyPrefix')} :
                      <Chip
                          label={getYesNoChipLabel(category.isEmergencyContact, t)}
                          color={getYesNoChipColor(category.isEmergencyContact)}
                          size="small"
                     />
                     </Typography>
                     </Box>
                  </TableCell>

                  <TableCell>
                    <Chip
                      size="small"
                      label={(category.categoryFor === 1 || category.categoryFor === '1' || category.categoryFor === true || category.categoryFor === 'true') ? t('common.active') : t('common.inactive')}
                      color={(category.categoryFor === 1 || category.categoryFor === '1' || category.categoryFor === true || category.categoryFor === 'true') ? 'success' : 'default'}
                      sx={{ fontWeight: 500 }}
                    />
                  </TableCell>

                  <TableCell>
                    <IconButton
                      onClick={() => handleOpen(category)}
                      sx={{ color: '#0F3D3E' }}
                    >
                      <Edit />
                    </IconButton>
                    {canDelete && (
                      <IconButton
                        onClick={() => handleDelete(category.id)}
                        sx={{ color: '#ef4444' }}
                      >
                        <Delete />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(Number.parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage={t('common.rowsPerPage')}
            sx={{
              '.MuiTablePagination-select': {
                color: '#0F3D3E',
              },
              '.MuiTablePagination-selectIcon': {
                color: '#0F3D3E',
              },
              '.MuiTablePagination-displayedRows': {
                color: '#0F3D3E',
              },
              '.MuiTablePagination-actions': {
                '& .MuiIconButton-root': {
                  color: '#0F3D3E',
                },
                '& .Mui-disabled': {
                  color: '#e9d5ff',
                }
              }
            }}
          />
        </TableContainer>
      )}

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md" fullScreen={isMobileView}>
        <DialogTitle>{getCategoryDialogTitle(selectedCategory, t)}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                name="title_EN"
                label={t('categoriesPage.fieldTitleEn')}
                fullWidth
                value={formData.title_EN}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                name="title_DE"
                label={t('categoriesPage.fieldTitleDe')}
                fullWidth
                value={formData.title_DE}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                name="discription_EN"
                label={t('categoriesPage.fieldDescEn')}
                fullWidth
                multiline
                rows={2}
                value={formData.discription_EN}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                name="discription_DE"
                label={t('categoriesPage.fieldDescDe')}
                fullWidth
                multiline
                rows={2}
                value={formData.discription_DE}
                onChange={handleChange}
              />
            </Grid>
             
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                name="backgroundColor"
                label={t('categoriesPage.fieldBg')}
                fullWidth
                type="color"
                value={formData.backgroundColor}
                onChange={handleChange}
                sx={{
                  '& input': {
                    height: 50
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                name="imageBackgroundColor"
                label={t('categoriesPage.fieldImgBg')}
                fullWidth
                type="color"
                value={formData.imageBackgroundColor}
                onChange={handleChange}
                sx={{
                  '& input': {
                    height: 50
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                name="icon"
                label={t('categoriesPage.fieldIcon')}
                fullWidth
                value={formData.icon}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={6} mt={2}>
            <CategoryBooleanSwitch
              name="isInfoIconAvailable"
              checked={formData.isInfoIconAvailable}
              label={t('categoriesPage.switchInfoIcon')}
              onChange={handleSwitchChange('isInfoIconAvailable')}
            />
            </Grid>

            <Grid item xs={12} sm={6} mt={2}>
            <CategoryBooleanSwitch
              name="hasContact"
              checked={formData.hasContact}
              label={t('categoriesPage.switchHasContact')}
              onChange={handleSwitchChange('hasContact')}
            />
            </Grid>

            <Grid item xs={12} sm={6} mt={2}>
            <CategoryBooleanSwitch
              name="isEmergencyContact"
              checked={formData.isEmergencyContact}
              label={t('categoriesPage.switchEmergency')}
              onChange={handleSwitchChange('isEmergencyContact')}
            />
            </Grid>

            <Grid item xs={12} sm={6} mt={2}>
            <CategoryBooleanSwitch
              name="categoryFor"
              checked={formData.categoryFor}
              label={t('categoriesPage.switchCategoryFor') || 'Category For'}
              onChange={handleSwitchChange('categoryFor')}
            />
            </Grid>
          </Grid>

        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isSubmitting}>{t('common.cancel')}</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : null}
          >
            {getCategorySubmitLabel(selectedCategory, t)}
          </Button>
        </DialogActions>
      </Dialog>


      <Snackbar
        open={toastOpen}
        autoHideDuration={3000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setToastOpen(false)} severity={toast.type} sx={{ width: '100%' }}>
          {toast.text}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Categories;
